import React, { useEffect, useRef, useState } from 'react';
import { Avatar, Button, Divider, ListItemIcon, ListItemText, ListSubheader, Menu, MenuItem, Typography } from '@mui/material';
import SettingsOutlinedIcon from '@mui/icons-material/SettingsOutlined';
import LanguageIcon from '@mui/icons-material/Language';
import LogoutIcon from '@mui/icons-material/Logout';
import PersonOutlineIcon from '@mui/icons-material/PersonOutline';
import AdminPanelSettingsOutlinedIcon from '@mui/icons-material/AdminPanelSettingsOutlined';
import AssessmentOutlinedIcon from '@mui/icons-material/AssessmentOutlined';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useUser } from './WebSocketContext';

/** Keep occasional account actions out of the workspace navigation. */
export default function SidebarAccountMenu({ isOpen, isUserLoggedIn, isPlatformAdmin, canAccessProcessingActivity,
  onProcessingActivityOpen, onNavigate, onLanguageMenuOpen, onLogout, tier }) {
  const { t, i18n } = useTranslation();
  const { username } = useUser();
  const [anchorEl, setAnchorEl] = useState(null);
  const buttonRef = useRef(null);
  const language = (i18n?.resolvedLanguage || i18n?.language || 'en').split('-')[0];
  const close = () => setAnchorEl(null);
  useEffect(() => { if (!isOpen) setAnchorEl(null); }, [isOpen]);
  const navigate = () => { close(); onNavigate?.(); };

  return <>
    <Button
      ref={buttonRef}
      id="sidebar-account-button"
      aria-label={t('sidebar.account_menu', 'Account and preferences')}
      aria-haspopup="menu"
      aria-expanded={Boolean(anchorEl)}
      aria-controls={anchorEl ? 'sidebar-account-menu' : undefined}
      onClick={(event) => setAnchorEl(event.currentTarget)}
      sx={{ minWidth: 56, minHeight: 44, px: 0.75, flexShrink: 0, color: 'inherit', borderRadius: 2,
        '&:hover': { bgcolor: 'rgba(255,255,255,0.14)' },
        '&.Mui-focusVisible': { outline: '2px solid currentColor', outlineOffset: 2 } }}
    >
      <Avatar sx={{ width: 30, height: 30, bgcolor: 'rgba(255,255,255,0.18)', color: 'inherit', fontSize: 14 }}>
        {isUserLoggedIn && username ? Array.from(username.trim())[0]?.toLocaleUpperCase(language) : <PersonOutlineIcon fontSize="small" />}
      </Avatar>
      <KeyboardArrowDownIcon sx={{ ml: 0.25, fontSize: 18 }} />
    </Button>
    <Menu
      id="sidebar-account-menu"
      anchorEl={anchorEl}
      open={Boolean(anchorEl)}
      onClose={close}
      anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      transformOrigin={{ vertical: 'top', horizontal: 'right' }}
      MenuListProps={{ 'aria-labelledby': 'sidebar-account-button' }}
      PaperProps={{ sx: { minWidth: 240, maxWidth: 'calc(100vw - 24px)', borderRadius: 2, mt: 0.5,
        '& .MuiMenuItem-root': { minHeight: 44, whiteSpace: 'normal' } } }}
    >
      {isUserLoggedIn && username && <ListSubheader sx={{ px: 2, py: 1, bgcolor: 'transparent', lineHeight: 'inherit' }}>
        <Typography variant="caption" color="text.secondary">{t('sidebar.signed_in_as', 'Signed in as')}</Typography>
        <Typography variant="body2" sx={{ fontWeight: 600, overflowWrap: 'anywhere' }}>{username}</Typography>
        {tier && <Typography variant="caption" color="text.secondary">{t('sidebar.plan_level', { level: tier })}</Typography>}
      </ListSubheader>}
      <MenuItem component={Link} to="/Settings" onClick={navigate}>
        <ListItemIcon><SettingsOutlinedIcon fontSize="small" /></ListItemIcon>
        <ListItemText>{t('sidebar.settings', 'Settings')}</ListItemText>
      </MenuItem>
      <MenuItem onClick={() => {
        close();
        // Anchor the existing language menu to the button, which stays mounted.
        onLanguageMenuOpen?.({ currentTarget: buttonRef.current });
      }}>
        <ListItemIcon><LanguageIcon fontSize="small" /></ListItemIcon>
        <ListItemText>{t('sidebar.language', 'Language')}</ListItemText>
        <Typography variant="body2" color="text.secondary" sx={{ ml: 2 }}>
          {t(language === 'fr' ? 'sidebar.language_french' : 'sidebar.language_english')}
        </Typography>
      </MenuItem>
      {isUserLoggedIn && isPlatformAdmin && <MenuItem component={Link} to="/platform" onClick={navigate}>
        <ListItemIcon><AdminPanelSettingsOutlinedIcon fontSize="small" /></ListItemIcon>
        <ListItemText>{t('saas.platform_title', 'Platform console')}</ListItemText>
      </MenuItem>}
      {canAccessProcessingActivity && <MenuItem onClick={() => { close(); onProcessingActivityOpen?.(); }}>
        <ListItemIcon><AssessmentOutlinedIcon fontSize="small" /></ListItemIcon>
        <ListItemText>{t('sidebar.processing_activity', 'Processing activity')}</ListItemText>
      </MenuItem>}
      {isUserLoggedIn && <Divider />}
      {isUserLoggedIn && <MenuItem onClick={() => { close(); onLogout?.(); }}>
        <ListItemIcon><LogoutIcon fontSize="small" /></ListItemIcon>
        <ListItemText>{t('sidebar.logout', 'Logout')}</ListItemText>
      </MenuItem>}
    </Menu>
  </>;
}
